import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { cursorToOffset, offsetToCursor } from "graphql-relay";
import { FilterIcon } from "lucide-react";
import { Suspense, useEffect, useTransition } from "react";
import {
	graphql,
	readInlineData,
	useLazyLoadQuery,
	useRefetchableFragment,
} from "react-relay";
import z from "zod";
import type { moderationFeed_commentFragment$key } from "@/components/__generated__/moderationFeed_commentFragment.graphql";
import type { moderationFeed_courseCommentFragment$key } from "@/components/__generated__/moderationFeed_courseCommentFragment.graphql";
import type { moderationFeed_postFragment$key } from "@/components/__generated__/moderationFeed_postFragment.graphql";
import {
	type FeedItem,
	ModerationFeed,
	moderationFeedCommentFragment,
	moderationFeedCourseCommentFragment,
	moderationFeedPostFragment,
	normalizeComment,
	normalizeCourseComment,
	normalizePost,
} from "@/components/moderation-feed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { moderacaoAllContentFragment$key } from "./__generated__/moderacaoAllContentFragment.graphql";
import type { moderacaoAllContentQuery } from "./__generated__/moderacaoAllContentQuery.graphql";
import type { moderacaoCommentsFragment$key } from "./__generated__/moderacaoCommentsFragment.graphql";
import type { moderacaoCommentsQuery } from "./__generated__/moderacaoCommentsQuery.graphql";
import type { moderacaoCourseCommentsFragment$key } from "./__generated__/moderacaoCourseCommentsFragment.graphql";
import type { moderacaoCourseCommentsQuery } from "./__generated__/moderacaoCourseCommentsQuery.graphql";
import type { moderacaoPageAllQuery } from "./__generated__/moderacaoPageAllQuery.graphql";
import type { moderacaoPageCommentsQuery } from "./__generated__/moderacaoPageCommentsQuery.graphql";
import type { moderacaoPageCourseCommentsQuery } from "./__generated__/moderacaoPageCourseCommentsQuery.graphql";
import type { moderacaoPagePostsQuery } from "./__generated__/moderacaoPagePostsQuery.graphql";
import type { moderacaoPostsFragment$key } from "./__generated__/moderacaoPostsFragment.graphql";
import type { moderacaoPostsQuery } from "./__generated__/moderacaoPostsQuery.graphql";

const PAGE_SIZE = 15;

const viewValues = [
	"todos",
	"posts",
	"comentarios",
	"comentarios-cursos",
] as const;
type ViewType = (typeof viewValues)[number];

const defaultValues = {
	view: "todos" as ViewType,
	page: 1,
	hideAnswered: false,
	hideAdminContent: false,
	hideAdminLiked: false,
	hideAdminAnswered: true,
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(general)/moderacao/",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			view: z.enum(viewValues).default("todos"),
			page: z.number().min(1).default(1),
			hideAnswered: z.boolean().default(false),
			hideAdminContent: z.boolean().default(false),
			hideAdminLiked: z.boolean().default(false),
			hideAdminAnswered: z.boolean().default(true),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function usePaginationHelpers() {
	const {
		page,
		hideAnswered,
		hideAdminContent,
		hideAdminLiked,
		hideAdminAnswered,
	} = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const [isPending, startTransition] = useTransition();

	const after =
		page === 1 ? undefined : offsetToCursor((page - 1) * PAGE_SIZE - 1);

	const contentFilters = {
		excludeWithResponses: hideAnswered,
		excludeAdminInteractions: hideAdminContent,
		excludeWithAdminVotes: hideAdminLiked,
		excludeWithAdminResponses: hideAdminAnswered,
	};

	const loadNext = () =>
		startTransition(() => {
			navigate({ search: (s) => ({ ...s, page: (s.page ?? 1) + 1 }) });
		});

	const loadPrevious = () =>
		startTransition(() => {
			navigate({ search: (s) => ({ ...s, page: (s.page ?? 2) - 1 }) });
		});

	return {
		page,
		after,
		isPending,
		startTransition,
		loadNext,
		loadPrevious,
		contentFilters,
		hideAnswered,
		hideAdminContent,
		hideAdminLiked,
		hideAdminAnswered,
	};
}

function getEdgeIndices(edges: ReadonlyArray<{ readonly cursor: string }>) {
	if (!edges.length) return { startIndex: 1, endIndex: 1 };
	return {
		startIndex: cursorToOffset(edges[0].cursor) + 1,
		endIndex: cursorToOffset(edges[edges.length - 1].cursor) + 1,
	};
}

// ── All Content View (Todos) ──────────────────────────────────────────

function AllContentView() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const {
		page,
		after,
		isPending,
		startTransition,
		loadNext,
		loadPrevious,
		contentFilters,
		hideAnswered,
		hideAdminContent,
		hideAdminLiked,
		hideAdminAnswered,
	} = usePaginationHelpers();

	const data = useLazyLoadQuery<moderacaoPageAllQuery>(
		graphql`
			query moderacaoPageAllQuery(
				$slug: String!
				$first: Int
				$after: String
				$excludeWithResponses: Boolean
				$excludeAdminInteractions: Boolean
				$excludeWithAdminVotes: Boolean
				$excludeWithAdminResponses: Boolean
			) {
				club(slug: $slug) {
					...moderacaoAllContentFragment
						@arguments(
							first: $first
							after: $after
							excludeWithResponses: $excludeWithResponses
							excludeAdminInteractions: $excludeAdminInteractions
							excludeWithAdminVotes: $excludeWithAdminVotes
							excludeWithAdminResponses: $excludeWithAdminResponses
						)
				}
			}
		`,
		{ slug: club?.slug ?? "", first: PAGE_SIZE, after, ...contentFilters },
	);

	const [content, refetch] = useRefetchableFragment<
		moderacaoAllContentQuery,
		moderacaoAllContentFragment$key
	>(
		graphql`
			fragment moderacaoAllContentFragment on Club
			@refetchable(queryName: "moderacaoAllContentQuery")
			@argumentDefinitions(
				first: { type: "Int" }
				after: { type: "String" }
				excludeWithResponses: { type: "Boolean" }
				excludeAdminInteractions: { type: "Boolean" }
				excludeWithAdminVotes: { type: "Boolean" }
				excludeWithAdminResponses: { type: "Boolean" }
			) {
				content(
					first: $first
					after: $after
					excludeWithResponses: $excludeWithResponses
					excludeAdminInteractions: $excludeAdminInteractions
					excludeWithAdminVotes: $excludeWithAdminVotes
					excludeWithAdminResponses: $excludeWithAdminResponses
				) {
					edges {
						node {
							__typename
							... on Post {
								...moderationFeed_postFragment
								...postCoverFragment

								coverContent {
								  __typename
								}
							}
							... on Comment {
								...moderationFeed_commentFragment
							}
							... on CourseComment {
								...moderationFeed_courseCommentFragment
							}
						}
						cursor
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						totalCount
					}
				}
			}
		`,
		data.club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					first: PAGE_SIZE,
					after:
						page === 1 ? undefined : offsetToCursor((page - 1) * PAGE_SIZE - 1),
					excludeWithResponses: hideAnswered,
					excludeAdminInteractions: hideAdminContent,
					excludeWithAdminVotes: hideAdminLiked,
					excludeWithAdminResponses: hideAdminAnswered,
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [
		page,
		hideAnswered,
		hideAdminContent,
		hideAdminLiked,
		hideAdminAnswered,
		refetch,
		startTransition,
	]);

	const items =
		content?.content?.edges
			.filter(Boolean)
			.map(({ node }) => {
				switch (node.__typename) {
					case "Post":
						return normalizePost(
							readInlineData<moderationFeed_postFragment$key>(
								moderationFeedPostFragment,
								node,
							),
							node,
						);
					case "Comment":
						return normalizeComment(
							readInlineData<moderationFeed_commentFragment$key>(
								moderationFeedCommentFragment,
								node,
							),
						);
					case "CourseComment":
						return normalizeCourseComment(
							readInlineData<moderationFeed_courseCommentFragment$key>(
								moderationFeedCourseCommentFragment,
								node,
							),
						);
					default:
						return null;
				}
			})
			.filter(Boolean) ?? [];

	const { startIndex, endIndex } = getEdgeIndices(
		content?.content?.edges ?? [],
	);

	return (
		<ModerationFeed
			onDeleted={() => {
				refetch({}, { fetchPolicy: "network-only" });
			}}
			data={items as FeedItem[]}
			isPending={isPending}
			totalCount={content?.content?.pageInfo.totalCount ?? 0}
			hasNextPage={Boolean(content?.content?.pageInfo.hasNextPage)}
			hasPreviousPage={Boolean(content?.content?.pageInfo.hasPreviousPage)}
			loadNext={loadNext}
			loadPrevious={loadPrevious}
			startIndex={startIndex}
			endIndex={endIndex}
		/>
	);
}

// ── Posts View ────────────────────────────────────────────────────────

function PostsView() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const {
		page,
		after,
		isPending,
		startTransition,
		loadNext,
		loadPrevious,
		contentFilters,
		hideAnswered,
		hideAdminContent,
		hideAdminLiked,
		hideAdminAnswered,
	} = usePaginationHelpers();

	const data = useLazyLoadQuery<moderacaoPagePostsQuery>(
		graphql`
			query moderacaoPagePostsQuery(
				$slug: String!
				$first: Int
				$after: String
				$excludeWithResponses: Boolean
				$excludeAdminInteractions: Boolean
				$excludeWithAdminVotes: Boolean
				$excludeWithAdminResponses: Boolean
			) {
				club(slug: $slug) {
					...moderacaoPostsFragment
						@arguments(
							first: $first
							after: $after
							excludeWithResponses: $excludeWithResponses
							excludeAdminInteractions: $excludeAdminInteractions
							excludeWithAdminVotes: $excludeWithAdminVotes
							excludeWithAdminResponses: $excludeWithAdminResponses
						)
				}
			}
		`,
		{ slug: club?.slug ?? "", first: PAGE_SIZE, after, ...contentFilters },
	);

	const [content, refetch] = useRefetchableFragment<
		moderacaoPostsQuery,
		moderacaoPostsFragment$key
	>(
		graphql`
			fragment moderacaoPostsFragment on Club
			@refetchable(queryName: "moderacaoPostsQuery")
			@argumentDefinitions(
				first: { type: "Int" }
				after: { type: "String" }
				excludeWithResponses: { type: "Boolean" }
				excludeAdminInteractions: { type: "Boolean" }
				excludeWithAdminVotes: { type: "Boolean" }
				excludeWithAdminResponses: { type: "Boolean" }
			) {
				content(
					first: $first
					after: $after
					type: POST
					excludeWithResponses: $excludeWithResponses
					excludeAdminInteractions: $excludeAdminInteractions
					excludeWithAdminVotes: $excludeWithAdminVotes
					excludeWithAdminResponses: $excludeWithAdminResponses
				) {
					edges {
						node {
							__typename
							... on Post {
								...moderationFeed_postFragment
								...postCoverFragment
							}
						}
						cursor
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						totalCount
					}
				}
			}
		`,
		data.club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					first: PAGE_SIZE,
					after:
						page === 1 ? undefined : offsetToCursor((page - 1) * PAGE_SIZE - 1),
					excludeWithResponses: hideAnswered,
					excludeAdminInteractions: hideAdminContent,
					excludeWithAdminVotes: hideAdminLiked,
					excludeWithAdminResponses: hideAdminAnswered,
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [
		page,
		hideAnswered,
		hideAdminContent,
		hideAdminLiked,
		hideAdminAnswered,
		refetch,
		startTransition,
	]);

	const items =
		content?.content?.edges
			.filter(Boolean)
			.flatMap(({ node }) =>
				node.__typename === "Post"
					? [
							normalizePost(
								readInlineData<moderationFeed_postFragment$key>(
									moderationFeedPostFragment,
									node,
								),
								node,
							),
						]
					: [],
			) ?? [];

	const { startIndex, endIndex } = getEdgeIndices(
		content?.content?.edges ?? [],
	);

	return (
		<ModerationFeed
			onDeleted={() => {
				refetch({}, { fetchPolicy: "network-only" });
			}}
			data={items}
			isPending={isPending}
			totalCount={content?.content?.pageInfo.totalCount ?? 0}
			hasNextPage={Boolean(content?.content?.pageInfo.hasNextPage)}
			hasPreviousPage={Boolean(content?.content?.pageInfo.hasPreviousPage)}
			loadNext={loadNext}
			loadPrevious={loadPrevious}
			startIndex={startIndex}
			endIndex={endIndex}
		/>
	);
}

// ── Comments View ─────────────────────────────────────────────────────

function CommentsView() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const {
		page,
		after,
		isPending,
		startTransition,
		loadNext,
		loadPrevious,
		contentFilters,
		hideAnswered,
		hideAdminContent,
		hideAdminLiked,
		hideAdminAnswered,
	} = usePaginationHelpers();

	const data = useLazyLoadQuery<moderacaoPageCommentsQuery>(
		graphql`
			query moderacaoPageCommentsQuery(
				$slug: String!
				$first: Int
				$after: String
				$excludeWithResponses: Boolean
				$excludeAdminInteractions: Boolean
				$excludeWithAdminVotes: Boolean
				$excludeWithAdminResponses: Boolean
			) {
				club(slug: $slug) {
					...moderacaoCommentsFragment
						@arguments(
							first: $first
							after: $after
							excludeWithResponses: $excludeWithResponses
							excludeAdminInteractions: $excludeAdminInteractions
							excludeWithAdminVotes: $excludeWithAdminVotes
							excludeWithAdminResponses: $excludeWithAdminResponses
						)
				}
			}
		`,
		{ slug: club?.slug ?? "", first: PAGE_SIZE, after, ...contentFilters },
	);

	const [content, refetch] = useRefetchableFragment<
		moderacaoCommentsQuery,
		moderacaoCommentsFragment$key
	>(
		graphql`
			fragment moderacaoCommentsFragment on Club
			@refetchable(queryName: "moderacaoCommentsQuery")
			@argumentDefinitions(
				first: { type: "Int" }
				after: { type: "String" }
				excludeWithResponses: { type: "Boolean" }
				excludeAdminInteractions: { type: "Boolean" }
				excludeWithAdminVotes: { type: "Boolean" }
				excludeWithAdminResponses: { type: "Boolean" }
			) {
				content(
					first: $first
					after: $after
					type: POST_COMMENT
					excludeWithResponses: $excludeWithResponses
					excludeAdminInteractions: $excludeAdminInteractions
					excludeWithAdminVotes: $excludeWithAdminVotes
					excludeWithAdminResponses: $excludeWithAdminResponses
				) {
					edges {
						node {
							__typename
							... on Comment {
								...moderationFeed_commentFragment
							}
						}
						cursor
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						totalCount
					}
				}
			}
		`,
		data.club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					first: PAGE_SIZE,
					after:
						page === 1 ? undefined : offsetToCursor((page - 1) * PAGE_SIZE - 1),
					excludeWithResponses: hideAnswered,
					excludeAdminInteractions: hideAdminContent,
					excludeWithAdminVotes: hideAdminLiked,
					excludeWithAdminResponses: hideAdminAnswered,
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [
		page,
		hideAnswered,
		hideAdminContent,
		hideAdminLiked,
		hideAdminAnswered,
		refetch,
		startTransition,
	]);

	const items =
		content?.content?.edges
			.filter(Boolean)
			.flatMap(({ node }) =>
				node.__typename === "Comment"
					? [
							normalizeComment(
								readInlineData<moderationFeed_commentFragment$key>(
									moderationFeedCommentFragment,
									node,
								),
							),
						]
					: [],
			) ?? [];

	const { startIndex, endIndex } = getEdgeIndices(
		content?.content?.edges ?? [],
	);

	return (
		<ModerationFeed
			onDeleted={() => {
				refetch({}, { fetchPolicy: "network-only" });
			}}
			data={items}
			isPending={isPending}
			totalCount={content?.content?.pageInfo.totalCount ?? 0}
			hasNextPage={Boolean(content?.content?.pageInfo.hasNextPage)}
			hasPreviousPage={Boolean(content?.content?.pageInfo.hasPreviousPage)}
			loadNext={loadNext}
			loadPrevious={loadPrevious}
			startIndex={startIndex}
			endIndex={endIndex}
		/>
	);
}

// ── Course Comments View ──────────────────────────────────────────────

function CourseCommentsView() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const {
		page,
		after,
		isPending,
		startTransition,
		loadNext,
		loadPrevious,
		contentFilters,
		hideAnswered,
		hideAdminContent,
		hideAdminLiked,
		hideAdminAnswered,
	} = usePaginationHelpers();

	const data = useLazyLoadQuery<moderacaoPageCourseCommentsQuery>(
		graphql`
			query moderacaoPageCourseCommentsQuery(
				$slug: String!
				$first: Int
				$after: String
				$excludeWithResponses: Boolean
				$excludeAdminInteractions: Boolean
				$excludeWithAdminVotes: Boolean
				$excludeWithAdminResponses: Boolean
			) {
				club(slug: $slug) {
					...moderacaoCourseCommentsFragment
						@arguments(
							first: $first
							after: $after
							excludeWithResponses: $excludeWithResponses
							excludeAdminInteractions: $excludeAdminInteractions
							excludeWithAdminVotes: $excludeWithAdminVotes
							excludeWithAdminResponses: $excludeWithAdminResponses
						)
				}
			}
		`,
		{ slug: club?.slug ?? "", first: PAGE_SIZE, after, ...contentFilters },
	);

	const [content, refetch] = useRefetchableFragment<
		moderacaoCourseCommentsQuery,
		moderacaoCourseCommentsFragment$key
	>(
		graphql`
			fragment moderacaoCourseCommentsFragment on Club
			@refetchable(queryName: "moderacaoCourseCommentsQuery")
			@argumentDefinitions(
				first: { type: "Int" }
				after: { type: "String" }
				excludeWithResponses: { type: "Boolean" }
				excludeAdminInteractions: { type: "Boolean" }
				excludeWithAdminVotes: { type: "Boolean" }
				excludeWithAdminResponses: { type: "Boolean" }
			) {
				content(
					first: $first
					after: $after
					type: COURSE_COMMENT
					excludeWithResponses: $excludeWithResponses
					excludeAdminInteractions: $excludeAdminInteractions
					excludeWithAdminVotes: $excludeWithAdminVotes
					excludeWithAdminResponses: $excludeWithAdminResponses
				) {
					edges {
						node {
							__typename
							... on CourseComment {
								...moderationFeed_courseCommentFragment
							}
						}
						cursor
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						totalCount
					}
				}
			}
		`,
		data.club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					first: PAGE_SIZE,
					after:
						page === 1 ? undefined : offsetToCursor((page - 1) * PAGE_SIZE - 1),
					excludeWithResponses: hideAnswered,
					excludeAdminInteractions: hideAdminContent,
					excludeWithAdminVotes: hideAdminLiked,
					excludeWithAdminResponses: hideAdminAnswered,
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [
		page,
		hideAnswered,
		hideAdminContent,
		hideAdminLiked,
		hideAdminAnswered,
		refetch,
		startTransition,
	]);

	const items =
		content?.content?.edges
			.filter(Boolean)
			.flatMap(({ node }) =>
				node.__typename === "CourseComment"
					? [
							normalizeCourseComment(
								readInlineData<moderationFeed_courseCommentFragment$key>(
									moderationFeedCourseCommentFragment,
									node,
								),
							),
						]
					: [],
			) ?? [];

	const { startIndex, endIndex } = getEdgeIndices(
		content?.content?.edges ?? [],
	);

	return (
		<ModerationFeed
			onDeleted={() => {
				refetch({}, { fetchPolicy: "network-only" });
			}}
			data={items}
			isPending={isPending}
			totalCount={content?.content?.pageInfo.totalCount ?? 0}
			hasNextPage={Boolean(content?.content?.pageInfo.hasNextPage)}
			hasPreviousPage={Boolean(content?.content?.pageInfo.hasPreviousPage)}
			loadNext={loadNext}
			loadPrevious={loadPrevious}
			startIndex={startIndex}
			endIndex={endIndex}
		/>
	);
}

// ── Filter Popover ────────────────────────────────────────────────────

const filterOptions = [
	{ key: "hideAnswered", label: "Ocultar conteúdo que já tem respostas" },
	{
		key: "hideAdminContent",
		label: "Ocultar conteúdo criado por administradores",
	},
	{
		key: "hideAdminLiked",
		label: "Ocultar conteúdo curtido por administradores",
	},
	{
		key: "hideAdminAnswered",
		label: "Ocultar conteúdo respondido por administradores",
	},
] as const;

function ModerationFilters() {
	const navigate = useNavigate({ from: Route.fullPath });
	const search = useSearch({ from: Route.id });

	const activeCount = filterOptions.filter((opt) => search[opt.key]).length;

	const toggleFilter = (key: string, checked: boolean) => {
		navigate({
			search: (s) => ({ ...s, [key]: checked, page: 1 }),
		});
	};

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button variant="outline" size="sm" className="gap-2">
					<FilterIcon className="size-4" />
					Filtrar
					{activeCount > 0 && (
						<Badge variant="outline" className="ml-1">
							{activeCount}
						</Badge>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-80">
				<div className="flex flex-col gap-4">
					<p className="font-medium text-sm">Filtros</p>
					{filterOptions.map((opt) => (
						// biome-ignore lint/a11y/noLabelWithoutControl: error
						<label
							key={opt.key}
							className="flex items-center justify-between gap-3 text-sm cursor-pointer"
						>
							<span>{opt.label}</span>
							<Switch
								checked={search[opt.key]}
								onCheckedChange={(v) => toggleFilter(opt.key, v)}
							/>
						</label>
					))}
				</div>
			</PopoverContent>
		</Popover>
	);
}

// ── Suspense Fallback ─────────────────────────────────────────────────

function FeedSkeleton() {
	return (
		<div className="flex flex-col gap-4 mt-4 max-w-[600px]">
			{Array.from({ length: 5 }, (_, i) => (
				<div key={i} className="border rounded-lg bg-card">
					<div className="flex items-start justify-between gap-2 px-5 pt-4 pb-2">
						<Skeleton className="h-4 w-64 bg-muted" />
						<Skeleton className="h-5 w-20 bg-muted rounded-full" />
					</div>
					<div className="px-5 pb-2">
						<Skeleton className="h-5 w-48 bg-muted" />
					</div>
					<div className="mx-5 mb-3">
						<Skeleton className="h-16 w-full bg-muted rounded-md" />
					</div>
					<div className="flex items-center justify-between px-5 pb-4 pt-1">
						<div className="flex items-center gap-4">
							<Skeleton className="h-4 w-10 bg-muted" />
							<Skeleton className="h-4 w-10 bg-muted" />
						</div>
						<Skeleton className="h-8 w-24 bg-muted rounded-md" />
					</div>
				</div>
			))}
		</div>
	);
}

// ── Main Route Component ──────────────────────────────────────────────

function RouteComponent() {
	const { view } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });

	return (
		<div className="p-6 flex justify-center w-full">
			<div className="w-full max-w-[600px]">
				<Tabs
					value={view}
					onValueChange={(val) =>
						navigate({
							search: (s) => ({
								...s,
								view: val as ViewType,
								page: 1,
							}),
						})
					}
				>
					<div className="flex flex-row items-center justify-between">
						<TabsList>
							<TabsTrigger value="todos" className="border-none">
								Todos
							</TabsTrigger>
							<TabsTrigger value="posts" className="border-none">
								Posts
							</TabsTrigger>
							<TabsTrigger value="comentarios" className="border-none">
								Comentários
							</TabsTrigger>
							<TabsTrigger value="comentarios-cursos" className="border-none">
								Comentários em Aulas
							</TabsTrigger>
						</TabsList>
						<ModerationFilters />
					</div>
					<TabsContent value="todos">
						<Suspense fallback={<FeedSkeleton />}>
							<AllContentView />
						</Suspense>
					</TabsContent>
					<TabsContent value="posts">
						<Suspense fallback={<FeedSkeleton />}>
							<PostsView />
						</Suspense>
					</TabsContent>
					<TabsContent value="comentarios">
						<Suspense fallback={<FeedSkeleton />}>
							<CommentsView />
						</Suspense>
					</TabsContent>
					<TabsContent value="comentarios-cursos">
						<Suspense fallback={<FeedSkeleton />}>
							<CourseCommentsView />
						</Suspense>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}
